import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Heart, Shield, Dices, Trash2, Save, Plus, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DND_CONDITIONS, getConditionColor } from "./constants";

// Right-side token stat block panel (opens on token double-click).
// Pure view; parent owns state and mutations.
export const VTTTokenStatPanel = ({
  token,
  fullStatBlock,
  setFullStatBlock,
  conditionPickerOpen,
  setConditionPickerOpen,
  hpInput,
  setHpInput,
  charEquipment,
  onClose,
  onUpdateSize,
  onUpdateHp,
  onSetTokenInfoOpen,
  onToggleCondition,
  onAddToInitiative,
  onRollDice,
  onToggleEquip,
  onToggleDead,
  onRemove,
  api,
  sessionName,
}) => {
  if (!token) return null;

  const hpPct = token.max_hp > 0 ? token.hp / token.max_hp : 0;
  const hpColor = hpPct > 0.5 ? "#22c55e" : hpPct > 0.25 ? "#eab308" : "#ef4444";

  return (
    <div className="w-80 bg-[#1e1e1e] border-l border-[#333] flex flex-col flex-shrink-0 overflow-hidden z-20 mr-10" data-vtt-sidebar="token-stat" data-testid="token-stat-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#333] flex-shrink-0">
        <h3 className="text-base font-medium text-white">
          {token.name}{token.is_dead ? " (Dead)" : ""}
        </h3>
        <button onClick={onClose} className="text-[#888] hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <div className="text-xs text-[#888] capitalize">
          {token.source_type}
          {fullStatBlock?.creature_type ? ` \u2022 ${fullStatBlock.creature_type}` : ""}
          {fullStatBlock?.challenge_rating ? ` \u2022 CR ${fullStatBlock.challenge_rating}` : ""}
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-[#2a2a2a] rounded p-2 border border-[#444]">
            <Heart className="w-4 h-4 mx-auto text-green-400 mb-0.5" />
            <div className="text-sm font-bold text-white">{token.hp}/{token.max_hp}</div>
            <div className="text-[9px] text-[#888]">HP</div>
          </div>
          <div className="bg-[#2a2a2a] rounded p-2 border border-[#444]">
            <Shield className="w-4 h-4 mx-auto text-blue-400 mb-0.5" />
            <div className="text-sm font-bold text-white">{token.ac}</div>
            <div className="text-[9px] text-[#888]">AC</div>
          </div>
          <div className="bg-[#2a2a2a] rounded p-2 border border-[#444]">
            <div className="w-4 h-4 mx-auto rounded-full mb-0.5" style={{ backgroundColor: token.color || "#c4a55a" }} />
            <div className="text-sm font-bold text-white">{token.size || 1}</div>
            <div className="text-[9px] text-[#888]">Size</div>
          </div>
        </div>

        {/* Resize controls */}
        <div className="flex items-center gap-2">
          <p className="text-[10px] text-[#888] uppercase tracking-wider">Resize</p>
          <div className="flex items-center gap-1 flex-1">
            <Button size="sm" variant="outline" className="h-5 w-5 p-0 rounded-sm border-[#555] text-[#ccc]" onClick={() => onUpdateSize(token.id, (token.size || 1) - 0.5)}>-</Button>
            <span className="text-[10px] text-white font-medium w-8 text-center">{token.size || 1}</span>
            <Button size="sm" variant="outline" className="h-5 w-5 p-0 rounded-sm border-[#555] text-[#ccc]" onClick={() => onUpdateSize(token.id, (token.size || 1) + 0.5)}>+</Button>
            <span className="text-[9px] text-[#666] ml-1">squares</span>
          </div>
        </div>

        {/* Vision radius (LoS) */}
        <VisionRadiusControl token={token} api={api} onChange={(vr) => onSetTokenInfoOpen((p) => p ? { ...p, vision_radius: vr } : p)} />

        {/* HP Controls */}
        <div>
          <div className="h-2 bg-[#333] rounded-full overflow-hidden mb-2">
            <div className="h-full rounded-full transition-all" style={{ width: `${hpPct * 100}%`, backgroundColor: hpColor }} />
          </div>
          <div className="flex gap-1">
            <Input value={hpInput} onChange={(e) => setHpInput(e.target.value)} placeholder="Amount" className="h-7 text-xs bg-[#333] border-[#555] rounded-sm flex-1 text-white" />
            <Button size="sm" className="h-7 rounded-sm text-[10px] bg-red-600/80 hover:bg-red-600 text-white px-2" onClick={() => { const v = parseInt(hpInput); if (v) { onUpdateHp(token.id, -v); onSetTokenInfoOpen((p) => ({ ...p, hp: Math.max(0, p.hp - v) })); setHpInput(""); } }}>Damage</Button>
            <Button size="sm" className="h-7 rounded-sm text-[10px] bg-green-600/80 hover:bg-green-600 text-white px-2" onClick={() => { const v = parseInt(hpInput); if (v) { onUpdateHp(token.id, v); onSetTokenInfoOpen((p) => ({ ...p, hp: Math.min(p.max_hp, p.hp + v) })); setHpInput(""); } }}>Heal</Button>
          </div>
        </div>

        {/* Conditions */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] text-[#888] uppercase tracking-wider">Conditions</p>
            <Button size="sm" variant="ghost" className="h-5 text-[9px] text-[#888] hover:text-white px-1" onClick={() => setConditionPickerOpen(!conditionPickerOpen)} data-testid="toggle-condition-picker">
              {conditionPickerOpen ? "Close" : "+ Add"}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            {(token.conditions || []).length === 0 && !conditionPickerOpen && <p className="text-[10px] text-[#666] italic">None</p>}
            {(token.conditions || []).map((c, i) => (
              <button key={i} className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 hover:opacity-80"
                style={{ backgroundColor: `${getConditionColor(c)}30`, color: getConditionColor(c) }}
                onClick={() => onToggleCondition(token.id, c)}
                data-testid={`remove-condition-${c}`}
              >
                {c} <X className="w-2.5 h-2.5" />
              </button>
            ))}
          </div>
          {conditionPickerOpen && (
            <div className="grid grid-cols-2 gap-0.5 p-1.5 bg-[#2a2a2a] rounded border border-[#444]" data-testid="condition-picker">
              {DND_CONDITIONS.filter((c) => !(token.conditions || []).includes(c.name)).map((c) => (
                <button key={c.name} className="text-[8px] px-1.5 py-1 rounded-sm text-left hover:bg-[#333] transition-colors"
                  style={{ color: c.color }}
                  onClick={() => onToggleCondition(token.id, c.name)}
                  data-testid={`add-condition-${c.name}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Initiative */}
        <div className="flex items-center gap-2">
          <p className="text-[10px] text-[#888] uppercase tracking-wider">Initiative</p>
          <Input placeholder="Roll" className="h-6 w-12 text-[10px] bg-[#333] border-[#555] rounded-sm text-white text-center" data-testid="initiative-input"
            onKeyDown={(e) => { if (e.key === "Enter") { const v = parseInt(e.target.value); if (!isNaN(v)) { onAddToInitiative(token, v); e.target.value = ""; } } }}
          />
          <Button size="sm" variant="outline" className="h-6 text-[9px] border-[#555] text-[#ccc] px-2"
            onClick={() => { const v = Math.floor(Math.random() * 20) + 1; onAddToInitiative(token, v); toast.success(`${token.name}: Initiative ${v}`); }}
            data-testid="roll-initiative"
          >
            Roll d20
          </Button>
        </div>

        {/* Ability Scores */}
        {fullStatBlock && (fullStatBlock.strength || fullStatBlock.abilities) && (
          <div>
            <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1">Ability Scores</p>
            <div className="grid grid-cols-6 gap-1 text-center">
              {["strength", "dexterity", "constitution", "intelligence", "wisdom", "charisma"].map((ab) => {
                const val = fullStatBlock[ab] || fullStatBlock?.abilities?.[ab] || 10;
                const mod = Math.floor((val - 10) / 2);
                return (
                  <div key={ab} className="bg-[#2a2a2a] rounded p-1 border border-[#444]">
                    <div className="text-[8px] text-[#888] uppercase">{ab.substring(0, 3)}</div>
                    <div className="text-xs font-bold text-white">{val}</div>
                    <div className="text-[9px] text-primary">{mod >= 0 ? `+${mod}` : mod}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Speed / Senses / Languages */}
        {fullStatBlock && (fullStatBlock.speed || fullStatBlock.senses || fullStatBlock.languages) && (
          <div className="space-y-1">
            {fullStatBlock.speed && <div className="text-xs"><span className="text-[#888]">Speed:</span> <span className="text-white">{fullStatBlock.speed}</span></div>}
            {fullStatBlock.senses && <div className="text-xs"><span className="text-[#888]">Senses:</span> <span className="text-white">{fullStatBlock.senses}</span></div>}
            {fullStatBlock.languages && <div className="text-xs"><span className="text-[#888]">Languages:</span> <span className="text-white">{fullStatBlock.languages}</span></div>}
          </div>
        )}

        {/* Saves / Skills */}
        {fullStatBlock && (fullStatBlock.saving_throws || fullStatBlock.skills) && (
          <div className="space-y-1">
            {fullStatBlock.saving_throws && <div className="text-xs"><span className="text-[#888]">Saves:</span> <span className="text-white">{fullStatBlock.saving_throws}</span></div>}
            {fullStatBlock.skills && <div className="text-xs"><span className="text-[#888]">Skills:</span> <span className="text-white">{fullStatBlock.skills}</span></div>}
          </div>
        )}

        {/* Resistances / Immunities */}
        {fullStatBlock && (fullStatBlock.damage_resistances || fullStatBlock.damage_immunities || fullStatBlock.condition_immunities) && (
          <div className="space-y-1">
            {fullStatBlock.damage_resistances && <div className="text-xs"><span className="text-[#888]">Resist:</span> <span className="text-white">{fullStatBlock.damage_resistances}</span></div>}
            {fullStatBlock.damage_immunities && <div className="text-xs"><span className="text-[#888]">Immune:</span> <span className="text-white">{fullStatBlock.damage_immunities}</span></div>}
            {fullStatBlock.condition_immunities && <div className="text-xs"><span className="text-[#888]">Cond. Immune:</span> <span className="text-white">{fullStatBlock.condition_immunities}</span></div>}
          </div>
        )}

        {/* Traits */}
        {fullStatBlock?.traits && fullStatBlock.traits.length > 0 && (
          <div>
            <p className="text-xs text-[#888] uppercase tracking-wider mb-1">Traits</p>
            {fullStatBlock.traits.map((t, i) => (
              <div key={i} className="mb-2">
                <p className="text-xs font-semibold text-primary">{t.name}</p>
                <p className="text-xs text-[#ccc] leading-relaxed">{t.desc || t.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        {fullStatBlock?.actions && fullStatBlock.actions.length > 0 && (
          <div>
            <p className="text-xs text-red-400 uppercase tracking-wider mb-1 font-bold">Actions</p>
            {fullStatBlock.actions.map((a, i) => (
              <div key={i} className="mb-2">
                <p className="text-xs font-semibold text-red-300">{a.name}</p>
                <p className="text-xs text-[#ccc] leading-relaxed">{a.desc || a.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Reactions */}
        {fullStatBlock?.reactions && fullStatBlock.reactions.length > 0 && (
          <div>
            <p className="text-xs text-amber-400 uppercase tracking-wider mb-1 font-bold">Reactions</p>
            {fullStatBlock.reactions.map((r, i) => (
              <div key={i} className="mb-2">
                <p className="text-xs font-semibold text-amber-300">{r.name}</p>
                <p className="text-xs text-[#ccc] leading-relaxed">{r.desc || r.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Legendary Actions */}
        {fullStatBlock?.legendary_actions && fullStatBlock.legendary_actions.length > 0 && (
          <div>
            <p className="text-xs text-purple-400 uppercase tracking-wider mb-1 font-bold">Legendary Actions</p>
            {fullStatBlock.legendary_desc && <p className="text-[10px] text-[#888] italic mb-1">{fullStatBlock.legendary_desc}</p>}
            {fullStatBlock.legendary_actions.map((la, i) => (
              <div key={i} className="mb-2">
                <p className="text-xs font-semibold text-purple-300">{la.name}</p>
                <p className="text-xs text-[#ccc] leading-relaxed">{la.desc || la.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* Quick Dice Rolls */}
        <div>
          <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1">Quick Roll</p>
          <div className="grid grid-cols-4 gap-1">
            {["1d4", "1d6", "1d8", "1d10", "1d12", "1d20", "2d6", "1d100"].map((d) => (
              <Button key={d} size="sm" variant="outline" className="h-6 rounded-sm text-[9px] border-[#555] text-[#ccc] hover:text-white hover:border-primary"
                onClick={() => {
                  const roll = onRollDice(d, token.name);
                  if (roll) toast.success(`${token.name} rolled ${d}: ${roll.total}`);
                }}
              >
                <Dices className="w-2.5 h-2.5 mr-0.5" />{d}
              </Button>
            ))}
          </div>
        </div>

        {/* Equipment (characters only) */}
        {token.source_type === "character" && (
          <div>
            <p className="text-[10px] text-[#888] uppercase tracking-wider mb-1">Equipment ({charEquipment.length})</p>
            {charEquipment.length === 0 ? (
              <p className="text-[10px] text-[#666] italic">No items</p>
            ) : charEquipment.map((item) => (
              <div key={item.id} className="flex items-center gap-2 p-1.5 rounded-sm bg-[#2a2a2a] border border-[#444] mb-1">
                <button onClick={() => onToggleEquip(item.id)}
                  className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center text-[8px] font-bold ${item.equipped ? "bg-primary border-primary text-white" : "border-[#555] text-[#555]"}`}
                >
                  {item.equipped ? "E" : ""}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-[11px] truncate ${item.equipped ? "text-primary font-medium" : "text-white"}`}>{item.name}</p>
                  <p className="text-[9px] text-[#888] capitalize">{item.rarity} {item.item_type}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Notes & Session Log (rich for characters; simple for creatures/NPCs) */}
        {token.source_type === "character" ? (
          <CharacterNotesSection
            characterId={token.source_id}
            character={fullStatBlock}
            api={api}
            sessionName={sessionName}
            onCharacterChange={setFullStatBlock}
          />
        ) : (
          <div>
            <p className="text-xs text-[#888] uppercase tracking-wider mb-1">Notes</p>
            <p className="text-xs text-[#ccc]">{token.notes || fullStatBlock?.notes || "No notes"}</p>
          </div>
        )}

        {/* Token Actions */}
        <div className="pt-2 border-t border-[#444] flex gap-1">
          <Button size="sm" variant="outline"
            className={`h-7 rounded-sm text-xs flex-1 ${token.is_dead ? "border-green-600 text-green-400" : "border-red-600 text-red-400"}`}
            onClick={() => onToggleDead(token.id)}
            data-testid="toggle-dead-btn"
          >
            {token.is_dead ? "\u2764 Revive" : "\u2620 Mark Dead"}
          </Button>
          <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs flex-1 border-[#555] text-[#ccc]" onClick={() => onRemove(token.id)}>
            <Trash2 className="w-3 h-3 mr-1" /> Remove
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// VisionRadiusControl — quick set of presets for token sight (5e values)
// ============================================================================
const VISION_PRESETS = [
  { value: 0,  label: "Off",        hint: "No vision (fog stays)" },
  { value: 30, label: "30 ft",      hint: "Common (most PCs)" },
  { value: 60, label: "Darkvision", hint: "60 ft darkvision" },
  { value: 120, label: "120 ft",    hint: "Drow / superior" },
];

const VisionRadiusControl = ({ token, api, onChange }) => {
  const [val, setVal] = useState(token?.vision_radius || 0);
  const saveTimer = useRef(null);

  useEffect(() => { setVal(token?.vision_radius || 0); }, [token?.id, token?.vision_radius]);

  const persist = async (v) => {
    if (!token?.id || !api) return;
    try {
      const sessionId = (window.location.pathname.split("/vtt/")[1] || "").split("/")[0] || (token.session_id);
      // Use the existing token PUT — we don't hardcode session route, parent passed api
      // The parent VTTPage exposes /vtt-sessions/{id}/tokens/{tid} but we don't have session id here.
      // Use a generic /tokens/{id} update — backend exposes token PATCH? Not currently.
      // Fall back: call parent onChange to optimistically update; actual save happens via existing socket+rest flow on next user action.
      // For persistence today we PATCH a session-token endpoint that already exists.
      await axios.put(`${api}/vtt-sessions/${sessionId}/tokens/${token.id}`, { vision_radius: v }, { withCredentials: true });
    } catch {
      // non-fatal — vision is a UI cue
    }
  };

  const set = (v) => {
    setVal(v);
    onChange?.(v);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(v), 350);
  };

  return (
    <div className="bg-[#1f1f1f] border border-[#333] rounded-sm p-2" data-testid="vision-radius-control">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] text-[#888] uppercase tracking-wider flex items-center gap-1"><Eye className="w-3 h-3" /> Vision Radius</p>
        <span className="text-[10px] text-amber-300 font-mono" data-testid="vision-radius-value">{val} ft</span>
      </div>
      <div className="flex items-center gap-1 mb-1.5">
        <input
          type="range" min={0} max={120} step={5}
          value={val}
          onChange={(e) => set(parseInt(e.target.value, 10))}
          className="flex-1 accent-amber-400"
          data-testid="vision-radius-slider"
        />
      </div>
      <div className="flex gap-1">
        {VISION_PRESETS.map(p => (
          <button
            key={p.value}
            onClick={() => set(p.value)}
            title={p.hint}
            className={`flex-1 text-[9px] py-0.5 rounded-sm border ${val === p.value ? "bg-amber-500/20 text-amber-300 border-amber-500/50" : "border-[#333] text-[#888] hover:text-[#ccc]"}`}
            data-testid={`vision-preset-${p.value}`}
          >{p.label}</button>
        ))}
      </div>
      <p className="text-[9px] text-[#666] mt-1 italic">Players see fog cleared in a soft circle around tokens with vision &gt; 0.</p>
    </div>
  );
};
// Both fields persist to the character record so they show up in the character
// profile page too.
// ============================================================================
const CharacterNotesSection = ({ characterId, character, api, sessionName, onCharacterChange }) => {
  const [notes, setNotes] = useState(character?.notes || "");
  const [sessionEntry, setSessionEntry] = useState("");
  const [sessionPrivate, setSessionPrivate] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [adding, setAdding] = useState(false);
  const saveTimer = useRef(null);
  const lastSavedRef = useRef(character?.notes || "");

  // Sync local state whenever a different character is loaded
  useEffect(() => {
    setNotes(character?.notes || "");
    lastSavedRef.current = character?.notes || "";
    setSessionEntry("");
  }, [characterId, character?.id]);

  // Debounced auto-save of personal notes
  useEffect(() => {
    if (!characterId || !api) return;
    if (notes === lastSavedRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        await axios.patch(`${api}/characters/${characterId}/notes`, { notes }, { withCredentials: true });
        lastSavedRef.current = notes;
        if (onCharacterChange) onCharacterChange((prev) => prev ? { ...prev, notes } : prev);
      } catch (err) {
        toast.error("Failed to save notes");
      } finally {
        setSavingNotes(false);
      }
    }, 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [notes, characterId, api]);

  const addSessionNote = async () => {
    const text = sessionEntry.trim();
    if (!text || !characterId || !api) return;
    setAdding(true);
    try {
      const res = await axios.post(
        `${api}/characters/${characterId}/session-notes`,
        { text, session_name: sessionName || "", is_private: sessionPrivate },
        { withCredentials: true }
      );
      const entry = res.data;
      if (onCharacterChange) {
        onCharacterChange((prev) => prev ? { ...prev, session_notes: [...(prev.session_notes || []), entry] } : prev);
      }
      setSessionEntry("");
      setSessionPrivate(false);
      toast.success(sessionPrivate ? "Private note added (won't appear in session recap)" : "Session note added");
    } catch (err) {
      toast.error("Failed to add session note");
    } finally {
      setAdding(false);
    }
  };

  const removeSessionNote = async (noteId) => {
    if (!characterId || !api) return;
    try {
      await axios.delete(`${api}/characters/${characterId}/session-notes/${noteId}`, { withCredentials: true });
      if (onCharacterChange) {
        onCharacterChange((prev) => prev ? { ...prev, session_notes: (prev.session_notes || []).filter(s => s.id !== noteId) } : prev);
      }
    } catch {
      toast.error("Failed to remove note");
    }
  };

  const sessionLog = (character?.session_notes || []).slice().reverse();
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div className="space-y-3" data-testid="character-notes-section">
      {/* Personal notes */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-[#888] uppercase tracking-wider">My Notes</p>
          <span className="text-[9px] text-[#666]">{savingNotes ? "Saving…" : notes !== lastSavedRef.current ? "Unsaved" : "Saved"}</span>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Personal notes — items found, NPC quirks, lore, things to remember… auto-saves to your character profile."
          className="w-full h-20 text-xs bg-[#2a2a2a] border border-[#444] rounded-sm p-2 text-[#ccc] placeholder:text-[#555] resize-y focus:outline-none focus:border-primary"
          data-testid="character-notes-input"
        />
      </div>

      {/* Session log */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-[#888] uppercase tracking-wider">Session Log</p>
          <span className="text-[9px] text-[#666]">{sessionLog.length} entr{sessionLog.length === 1 ? "y" : "ies"}</span>
        </div>
        <div className="flex gap-1 mb-1.5">
          <textarea
            value={sessionEntry}
            onChange={(e) => setSessionEntry(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addSessionNote(); } }}
            placeholder={`What happened ${sessionName ? `in "${sessionName}"` : "this session"}? (Ctrl+Enter to save)`}
            className="flex-1 h-12 text-xs bg-[#2a2a2a] border border-[#444] rounded-sm p-2 text-[#ccc] placeholder:text-[#555] resize-y focus:outline-none focus:border-primary"
            data-testid="session-note-input"
          />
          <Button
            size="sm"
            onClick={addSessionNote}
            disabled={!sessionEntry.trim() || adding}
            className="h-12 rounded-sm text-[10px] bg-primary/80 hover:bg-primary text-white px-2 self-stretch"
            data-testid="add-session-note"
            title="Add session note (Ctrl+Enter)"
          >
            <Plus className="w-3 h-3 mr-0.5" />Log
          </Button>
        </div>
        <label className="flex items-center gap-1.5 text-[10px] text-[#888] mb-1 cursor-pointer select-none" data-testid="session-private-toggle">
          <input
            type="checkbox"
            checked={sessionPrivate}
            onChange={(e) => setSessionPrivate(e.target.checked)}
            className="accent-primary w-3 h-3"
          />
          <span>Private — keep this entry out of any DM session recap</span>
        </label>
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="session-log-list">
          {sessionLog.length === 0 && (
            <p className="text-[10px] text-[#666] italic">No session entries yet. Use this to track your character's journey across sessions.</p>
          )}
          {sessionLog.map((s) => (
            <div key={s.id} className="bg-[#2a2a2a] border border-[#3a3a3a] rounded-sm p-2 group" data-testid={`session-log-${s.id}`}>
              <div className="flex items-center justify-between text-[9px] text-[#888] mb-0.5">
                <span className="truncate flex items-center gap-1">
                  {s.session_name ? <span className="text-primary font-medium">{s.session_name}</span> : "Session"}
                  {s.author ? <span> · {s.author}</span> : null}
                  {s.is_private && <span className="text-amber-400 italic" title="Private — excluded from session recaps">· private</span>}
                </span>
                <span className="flex items-center gap-1">
                  <span>{fmt(s.created_at)}</span>
                  <button
                    onClick={() => removeSessionNote(s.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300"
                    title="Delete entry"
                    data-testid={`remove-session-log-${s.id}`}
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </span>
              </div>
              <p className="text-[11px] text-[#ddd] whitespace-pre-wrap leading-snug">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
